import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Input,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { SortHeader, getHighlightedText } from "../../utils/helpers";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

import { ReactComponent as GlobeSvg } from "../../assets/Globe.svg";

// ======= tweakables =======
const UI = {
  border: "#E6E8EB",
  statusInactive: "rgba(0,0,0,0.38)",
};

// ---------- dummy data ----------
const CUSTOMERS = [
  "DELTA Tech",
  "Customer Company Name Ltd.",
  "Acme Industries GmbH",
  "Globex AG",
  "Umbrella Solutions",
  "Initech Corp.",
  "Soylent Systems",
  "Hooli Europe",
  "Wayne Enterprises",
  "Stark Manufacturing",
  "Wonka Foods GmbH",
  "Tyrell Robotics",
];

const PORTAL_STATUSES = ["veröffentlicht", "in Planung", "offline", "nicht eingerichtet"];

const makeRow = (id) => ({
  id,
  name: CUSTOMERS[id % CUSTOMERS.length],
  status: PORTAL_STATUSES[id % PORTAL_STATUSES.length],
});

const ALL_ROWS = Array.from({ length: 78 }, (_, i) => makeRow(i + 1));

export default function KundenPortalTable() {
  const i18n = useSelector((state) => state.i18n);
  const uiLanguage = useSelector((state) => state.uiLanguage || "de");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle"); // alle | one of PORTAL_STATUSES
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();

  // normalize helper (handles diacritics)
  const norm = (v) =>
    String(v).toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

  // reset to page 1 on filter/search change
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  // ----- filtering -----
  const filtered = useMemo(() => {
    const tokens = norm(search).split(/\s+/).filter(Boolean);
    return ALL_ROWS.filter((r) => {
      const searchable = [r.name, r.status].map(norm).join(" ");
      const matchesSearch = tokens.length === 0 || tokens.every((t) => searchable.includes(t));
      const matchesStatus = statusFilter === "alle" || r.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [search, statusFilter]);

  // ----- sorting -----
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = a[orderBy];
      const vb = b[orderBy];
      let comp = 0;
      if (va < vb) comp = -1;
      if (va > vb) comp = 1;
      return order === "asc" ? comp : -comp;
    });
  }, [filtered, orderBy, order]);

  // ----- pagination -----
  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  const statusLabels = {
    de: {
      veröffentlicht: "veröffentlicht",
      "in Planung": "in Planung",
      offline: "offline",
      "nicht eingerichtet": "nicht eingerichtet",
      alle: "alle",
    },
    en: {
      veröffentlicht: "published",
      "in Planung": "planned",
      offline: "offline",
      "nicht eingerichtet": "not configured",
      alle: "all",
    },
  };


  return (
    <Box className=" bxShadow">
      {/* Header bar */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 3,
          pt: 3,
          pb:1
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: 20, color: "var(--color-primary)",fontFamily:"Rubik" }}>
          {i18n?.customerPortal_tableTitle} ({filtered.length})
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* search */}
        <FormControl sx={{ width: 360 }} variant="standard">
            <Input
            fullWidth
            placeholder={i18n?.customerPortal_searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton tabIndex={-1}>
                  <Search />
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        {/* status select */}
        <FormControl sx={{ minWidth: 180, ml: 2 }} variant="standard">
          <Select
            label={i18n?.customerPortal_statusLabel}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="alle">{i18n?.customerPortal_statusAll}</MenuItem>
            {PORTAL_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {statusLabels[uiLanguage]?.[s] || s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <Table
      className="kundentable_wrapper"
        stickyHeader
        aria-label={i18n?.customerPortal_tableTitle}
        sx={{
            padding: "0px 24px",
          "& th, & td": { borderBottom: `1px solid ${UI.border}`, px: 3 },
          "& th": {
            bgcolor: "#fff",
            color: "var(--color-primary)",
            fontWeight: 500,
            fontSize: 16,
            letterSpacing: 0.15,
            padding: "24px 24px 16px 0px",
          },
          "& td": {
            color: "#38748D",
            fontSize: 16,
            letterSpacing: 0.5,
            padding: "16px 24px 16px 0px",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "60%", pl: 0 }}>
              <SortHeader id="name" orderBy={orderBy} order={order} setOrderBy={setOrderBy} setOrder={setOrder} onClick={() => handleSort("name")}>
                {i18n?.nav?.customerPortal || i18n?.customerPortal_tableTitle}
              </SortHeader>
            </TableCell>
            <TableCell sx={{ width: "40%" }}>
              <SortHeader id="status" orderBy={orderBy} order={order} setOrderBy={setOrderBy} setOrder={setOrder} onClick={() => handleSort("status")}>
                {i18n?.customerPortal_statusLabel}
              </SortHeader>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginated.map((row) => (
            <TableRow key={row.id} hover>
              {/* Kundenportal with globe icon */}
              <TableCell sx={{ pl: 0 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <GlobeSvg/>
                  <Typography sx={{ fontWeight: 500, color: "#0E6AA0" }}>
                    {getHighlightedText(row.name, search)}
                  </Typography>
                </Stack>
              </TableCell>

              {/* Status with dotted underline, grey if offline */}
              <TableCell>
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: row.status === "offline" ? UI.statusInactive : "text.primary",
                    borderBottom: "1px dotted",
                    borderColor: row.status === "offline" ? UI.statusInactive : "rgba(0,0,0,0.87)",
                    display: "inline-block",
                  }}
                >
                  {getHighlightedText(statusLabels[uiLanguage]?.[row.status] || row.status, search)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 3,
          color: "var(--color-primary)",
        }}
      >
        <Pagination
          count={Math.max(1, Math.ceil(filtered.length / rowsPerPage))}
          page={page + 1}
          onChange={(_, value) => setPage(value - 1)}
          color="text"
          shape="rounded"
        />

        <FormControl variant="standard" sx={{ m: 1, minWidth: 220 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ color: "rgba(0, 0, 0, 0.5)" }}
          >
            {[10, 25, 50].map((size) => (
              <MenuItem key={size} value={size}>
                {i18n?.customerPortal_entriesPerPage?.replace("%s", size)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>

  );
}
